import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../config/supabase'
import Footer from '../components/Footer'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'

const Admin = () => {
  const { user } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('payments')
  const [payments, setPayments] = useState([])
  const [announcements, setAnnouncements] = useState([])
  const [newAnnouncement, setNewAnnouncement] = useState('')
  const [stats, setStats] = useState({
    todayOrders: 0,
    pendingOrders: 0,
    totalOrders: 0
  });
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount_amount: '',
    discount_type: 'fixed',
    min_purchase: '0',
    max_discount: '',
    usage_limit: '1',
    expires_at: ''
  });
  const [emailSearch, setEmailSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [daysToModify, setDaysToModify] = useState(0);
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: ''
  });

  // เพิ่ม state สำหรับจัดการ pagination
  const [currentUserPage, setCurrentUserPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const USERS_PER_PAGE = 10;

  // เพิ่ม state สำหรับเก็บสถิติสมาชิก
  const [memberStats, setMemberStats] = useState({
    total: 0,
    free: 0,
    vip: 0,
    lifetime: 0,
    admin: 0
  });

  // เพิ่ม state สำหรับเก็บข้อมูลที่กำลังแก้ไข
  const [editingUserData, setEditingUserData] = useState(null);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers()
    } else if (activeTab === 'payments') {
      fetchPayments()
    } else if (activeTab === 'announcements') {
      getAnnouncements()
    } else if (activeTab === 'coupons') {
      getCoupons()
    }
  }, [activeTab])

  useEffect(() => {
    getStats();
  }, [dateRange]);

  useEffect(() => {
    console.log('Active Tab:', activeTab); // Debug log
    if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab, currentUserPage]);

  const getUsers = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setUsers(data)
    } catch (error) {
      console.error('Error loading users:', error.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchPayments = async () => {
    try {
      setLoading(true);
      
      // ดึงข้อมูลการชำระเงิน
      const { data: paymentsData, error: paymentsError } = await supabase
        .from('payments')
        .select('*')
        .order('created_at', { ascending: false });

      if (paymentsError) throw paymentsError;

      // ดึงข้อมูลโปรไฟล์พร้อมวันหมดอายุ
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, email, display_name, expires_at');

      if (profilesError) throw profilesError;

      // ดึงข้อมูลแพ็กเกจ
      const { data: packagesData, error: packagesError } = await supabase
        .from('packages')
        .select('id, name');

      if (packagesError) throw packagesError;

      // รวมข้อมู������ทั้งหมด
      const combinedData = paymentsData.map(payment => ({
        ...payment,
        profiles: profilesData.find(profile => profile.id === payment.user_id),
        packages: packagesData.find(pkg => pkg.id === payment.package_id)
      }));

      setPayments(combinedData);
    } catch (error) {
      console.error('Error loading payments:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const getAnnouncements = async () => {
    try {
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error
      setAnnouncements(data)
    } catch (error) {
      console.error('Error loading announcements:', error)
    }
  }

  const createAnnouncement = async () => {
    try {
      if (!newAnnouncement.trim()) return

      const { error } = await supabase
        .from('announcements')
        .insert({
          message: newAnnouncement,
          is_active: true,
          created_by: user.id
        });

      if (error) throw error
      
      setNewAnnouncement('')
      getAnnouncements()
    } catch (error) {
      console.error('Error creating announcement:', error)
    }
  }

  const toggleAnnouncementStatus = async (id, currentStatus) => {
    try {
      const { error } = await supabase
        .from('announcements')
        .update({ is_active: !currentStatus })
        .eq('id', id)

      if (error) throw error
      getAnnouncements()
    } catch (error) {
      console.error('Error updating announcement:', error)
    }
  }

  const updateMembership = async (userId, newMembership) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ membership: newMembership })
        .eq('id', userId)

      if (error) throw error
      
      // Refresh users list
      getUsers()
    } catch (error) {
      console.error('Error updating membership:', error.message)
    }
  }

  const updatePaymentStatus = async (paymentId, status) => {
    try {
      // 1. ดึงข้อมูลการชำระเงิน
      const { data: paymentData, error: fetchError } = await supabase
        .from('payments')
        .select('id, user_id, package_id, payment_status')
        .eq('id', paymentId)
        .single();

      if (fetchError) throw fetchError;
      console.log('Payment Data:', paymentData);

      // 2. อัพเดทสถานะการชำระเงิน
      const { error: updateError } = await supabase
        .from('payments')
        .update({ payment_status: status })
        .eq('id', paymentId);

      if (updateError) throw updateError;

      // 3. ถ้าอนุมัติ ให้อัพเดทสถานะสมาชิก
      if (status === 'approved') {
        try {
          // ดึงข้อมูลแพ็กเกจ
          const { data: packageData, error: packageError } = await supabase
            .from('packages')
            .select('name')
            .eq('id', paymentData.package_id)
            .single();

          if (packageError) throw packageError;
          console.log('Package Data:', packageData);

          let membershipType = 'vip'; // ค่าเริ่มต้นเป็น vip
          let expiryDate = new Date();

          // กำหนดประเภทสมาชิกและวันหมดอายุ
          if (packageData.name.toLowerCase().includes('ตลอดชีพ')) {
            membershipType = 'lifetime';
            expiryDate = new Date(9999, 11, 31);
          } else if (packageData.name.toLowerCase().includes('1 ปี')) {
            expiryDate.setFullYear(expiryDate.getFullYear() + 1);
          } else {
            expiryDate.setMonth(expiryDate.getMonth() + 1);
          }

          // สร้าง SQL query สำหรับอัพเดทโปรไฟล์
          const { error: profileError } = await supabase.rpc(
            'update_member_profile',
            {
              user_id: paymentData.user_id,
              new_membership: membershipType,
              new_expires_at: expiryDate.toISOString()
            }
          );

          if (profileError) throw profileError;

          // อัพเดทค่า has_purchased_vip แยกต่างหาก
          const { error: updateVipError } = await supabase
            .from('profiles')
            .update({ has_purchased_vip: true })
            .eq('id', paymentData.user_id);

          if (updateVipError) throw updateVipError;

          console.log('Profile updated successfully');

        } catch (innerError) {
          console.error('Inner Error:', innerError);
          throw innerError;
        }
      }

      // รีเฟรชข้อมูล
      fetchPayments()
      alert('อนุมัติการชำระเงินเรียบร้อย')

    } catch (error) {
      console.error('Error:', error);
      showNotification('เกิดข้อผิดพลาดในการอัพเดทสถานะ', 'error');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('th-TH', {
      year: '2-digit',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStats = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // ดึงยอดสามช่วงวันที่ที่เลือก
      const { data: rangeData, error: rangeError } = await supabase
        .from('payments')
        .select('amount')
        .gte('created_at', dateRange.startDate)
        .lte('created_at', dateRange.endDate + 'T23:59:59')
        .eq('payment_status', 'approved');

      if (rangeError) throw rangeError;

      // ดึงยอดรออนุมัติ
      const { data: pendingData, error: pendingError } = await supabase
        .from('payments')
        .select('amount')
        .eq('payment_status', 'pending');

      if (pendingError) throw pendingError;

      // ดึงยอดทั้งมด
      const { data: totalData, error: totalError } = await supabase
        .from('payments')
        .select('amount')
        .eq('payment_status', 'approved');

      if (totalError) throw totalError;

      // คำนวณยอดรวม
      const rangeTotal = rangeData.reduce((sum, item) => sum + item.amount, 0);
      const pendingTotal = pendingData.reduce((sum, item) => sum + item.amount, 0);
      const allTimeTotal = totalData.reduce((sum, item) => sum + item.amount, 0);

      setStats({
        todayOrders: rangeTotal,
        pendingOrders: pendingTotal,
        totalOrders: allTimeTotal
      });

    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // เพิ่มฟังก์ชันำหรับจัดการปุ่มลัด
  const handleDatePreset = (preset) => {
    const today = new Date();
    let startDate = new Date();
    let endDate = new Date();
    
    switch (preset) {
      case 'today':
        // วันนี้ (ตั้งเวลาเริ่มต้นเป็น 00:00:00)
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'yesterday':
        // เมื่อวาน
        startDate.setDate(today.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'last7days':
        // 7 วันที่แล้ว
        startDate.setDate(today.getDate() - 6);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'thisMonth':
        // เดือนนี้
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'lastMonth':
        // เดืนนนนนน่แล้ว
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      default:
        return;
    }

    setDateRange({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    });
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('ต้องกรลบข่าวสารนี้ใช่หรือไม่?')) return;
    
    try {
      const { error } = await supabase
        .from('announcements')
        .delete()
        .eq('id', id);

      if (error) throw error;
      getAnnouncements();
    } catch (error) {
      console.error('Error deleting announcement:', error);
    }
  };

  const updateAnnouncement = async (id, newMessage) => {
    try {
      const { error } = await supabase
        .from('announcements')
        .update({ message: newMessage })
        .eq('id', id);

      if (error) throw error;
      setEditingAnnouncement(null);
      getAnnouncements();
    } catch (error) {
      console.error('Error updating announcement:', error);
    }
  };

  const getCoupons = async () => {
    try {
      const { data, error } = await supabase
        .from('coupons')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCoupons(data);
    } catch (error) {
      console.error('Error loading coupons:', error);
    }
  };

  const createCoupon = async () => {
    try {
      const { error } = await supabase
        .from('coupons')
        .insert({
          ...newCoupon,
          min_purchase: Number(newCoupon.min_purchase),
          max_discount: newCoupon.max_discount ? Number(newCoupon.max_discount) : null,
          discount_amount: Number(newCoupon.discount_amount),
          usage_limit: Number(newCoupon.usage_limit)
        });

      if (error) throw error;
      
      // รีเซ็ตฟอร์ม
      setNewCoupon({
        code: '',
        discount_amount: '',
        discount_type: 'fixed',
        min_purchase: '0',
        max_discount: '',
        usage_limit: '1',
        expires_at: ''
      });
      
      getCoupons();
    } catch (error) {
      console.error('Error creating coupon:', error);
      alert('เกิดข้อผิดพลาด กรุณาลองใหม่');
    }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm('ต้องการลบคูปองนี้ใช่หรือไม่?')) return;
    
    try {
      const { error } = await supabase
        .from('coupons')
        .delete()
        .eq('id', id);

      if (error) throw error;
      getCoupons();
    } catch (error) {
      console.error('Error deleting coupon:', error);
      alert('เกิดข้อผิดพลาด กรุณาลองใหม่');
    }
  };

  const toggleCouponStatus = async (id, currentStatus) => {
    try {
      const { error } = await supabase
        .from('coupons')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;
      getCoupons();
    } catch (error) {
      console.error('Error updating coupon status:', error);
    }
  };

  const searchUserByEmail = async () => {
    try {
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('*')
        .ilike('email', `%${emailSearch}%`)
        .single();

      if (userError) throw userError;
      setSelectedUser(userData);
    } catch (error) {
      console.error('Error searching user:', error);
      showNotification('ไม่พบผู้ใช้งานที่ค้นหา', 'error');
    }
  };

  const updateVIPDays = async (action) => {
    if (!selectedUser) return;

    try {
      const currentDate = selectedUser.expires_at ? new Date(selectedUser.expires_at) : new Date();
      const newDate = new Date(currentDate);
      
      if (action === 'add') {
        newDate.setDate(newDate.getDate() + parseInt(daysToModify));
      } else {
        newDate.setDate(newDate.getDate() - parseInt(daysToModify));
      }

      const { error } = await supabase
        .from('profiles')
        .update({ 
          membership: 'vip',
          expires_at: newDate.toISOString()
        })
        .eq('id', selectedUser.id);

      if (error) throw error;

      // อัพเดทข้อมูลผู้ใช้ที่แสดง
      const { data: updatedUser } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', selectedUser.id)
        .single();

      setSelectedUser(updatedUser);
      showNotification('อัพเดทัน VIP สำเร็จ', 'success');
    } catch (error) {
      console.error('Error updating VIP days:', error);
      showNotification('เกิดข้อผิดพลาดนการอัพเดทวัน VIP', 'error');
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({
      show: true,
      message,
      type
    });

    // ซ่อนการแจ้งเตือนหลังจาก 3 วินาที
    setTimeout(() => {
      setNotification({
        show: false,
        message: '',
        type: ''
      });
    }, 3000);
  };

  const handleApprove = async (payment) => {
    try {
      // เช็คว่าเป็นการต่ออายุห���ือไม่
      const isRenewal = payment.package_id === 3; // package_id 3 คือแพ็กเกจต่ออายุ

      // ดึงข้อมูล profile ปัจจุบัน
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('expires_at, membership')
        .eq('id', payment.user_id)
        .single();

      if (profileError) throw profileError;

      let newExpiredDate;
      let membershipType = 'vip'; // ค่าเริ่มต้น

      if (isRenewal) {
        // กรณีต่ออายุ: ต่อจากวันหมดอายุเดิม หรือวันปัจจุบันถ้าหมดอายุไปแล้ว
        const currentExpiredDate = profileData.expires_at 
          ? new Date(profileData.expires_at) 
          : new Date();

        // ถ้าวันหมดอายุเก่าน้อยกว่าวันปัจจุบัน ให้เริ่มนับจากวันปัจจุบัน
        const startDate = currentExpiredDate < new Date() ? new Date() : currentExpiredDate;
        newExpiredDate = new Date(startDate);
        newExpiredDate.setMonth(newExpiredDate.getMonth() + 1);
      } else {
        // กรณีสมั���รใหม่: คำนวณตามประเภทแพ็กเกจ
        newExpiredDate = new Date();
        switch (payment.package_id) {
          case 1: // รายเดือน
            newExpiredDate.setMonth(newExpiredDate.getMonth() + 1);
            break;
          case 2: // รายปี
            newExpiredDate.setFullYear(newExpiredDate.getFullYear() + 1);
            break;
          case 4: // ตลอดชีพ
            membershipType = 'lifetime';
            newExpiredDate = new Date('2099-12-31');
            break;
        }
      }

      // อัพเดทสถานะการชำระเงิน
      const { error: paymentError } = await supabase
        .from('payments')
        .update({ payment_status: 'approved' })
        .eq('id', payment.id);

      if (paymentError) throw paymentError;

      // อัพเดท profile
      const { error: profileUpdateError } = await supabase
        .from('profiles')
        .update({
          membership: membershipType,
          expires_at: newExpiredDate.toISOString()
        })
        .eq('id', payment.user_id);

      if (profileUpdateError) throw profileUpdateError;

      // อัพเดทค่า has_purchased_vip แยกต่างหาก
      const { error: updateVipError } = await supabase
        .from('profiles')
        .update({ has_purchased_vip: true })
        .eq('id', payment.user_id);

      if (updateVipError) throw updateVipError;

      // รีเฟรชข้อมูล
      fetchPayments();
      showNotification('อนุมัติการชำระเงินเรียบร้อย', 'success');

    } catch (error) {
      console.error('Error approving payment:', error);
      showNotification('เกิดข้อผิดพลาดในการอนุมัติ: ' + error.message, 'error');
    }
  };

  // เพิ่มฟังก์ชันสำหรับลบผู้ใช้
  const deleteUser = async (userId) => {
    if (!window.confirm('ต้องการลบผู้ใช้นี้ใช่หรือไม่?')) return;

    try {
      // ตรวจสอบว่าเป็น admin
      const { data: adminCheck } = await supabase
        .from('profiles')
        .select('membership')
        .eq('id', user.id)
        .single();

      if (adminCheck?.membership !== 'admin') {
        throw new Error('ไม่มีสิทธิ์ในการลบ');
      }

      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);

      if (error) throw error;
      
      fetchUsers();
      showNotification('ลบผู้ใช้สำเร็จ', 'success');
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotification('เกิดข้อผิดพลาดในการลบผู้ใ��้: ' + error.message, 'error');
    }
  };

  // เพิ่มฟังก์ชันสำหรับแก้ไขผู้ใช้
  const editUser = async (userId) => {
    try {
      const { data: userData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;

      // เซ็ตข้อมูลผู้ใช้ที่กำลังแก้ไข
      setEditingUserData(userData);
    } catch (error) {
      console.error('Error fetching user:', error);
      showNotification('เกิดข้อผิดพลาดในการดึงข้อมูลผู้ใช้', 'error');
    }
  };

  // แก้ไขฟังก์ชัน saveUserEdit
  const saveUserEdit = async () => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          display_name: editingUserData.display_name,
          email: editingUserData.email,
          expires_at: editingUserData.expires_at, // เพิ่มการอัพเดทวันหมดอายุ
        })
        .eq('id', editingUserData.id);

      if (error) throw error;

      setEditingUserData(null);
      fetchUsers();
      showNotification('บันทึกการแก้ไขสำเร็จ', 'success');
    } catch (error) {
      console.error('Error saving user:', error);
      showNotification('เกิดข้อผิดพลาดในการบันทึกข้อมูล: ' + error.message, 'error');
    }
  };

  // เพิ่มฟังก์ชัน fetchUsers
  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      let queryBuilder = supabase
        .from('profiles')
        .select('*', { count: 'exact' });

      if (emailSearch) {
        queryBuilder = queryBuilder.ilike('email', `%${emailSearch}%`);
      }

      const from = (currentUserPage - 1) * USERS_PER_PAGE;
      const to = from + USERS_PER_PAGE - 1;

      const { data, count, error } = await queryBuilder
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      setUsers(data || []);
      setTotalUsers(count || 0);

      const memberCounts = {
        total: count || 0,
        free: 0,
        vip: 0,
        lifetime: 0,
        admin: 0
      };

      data?.forEach(user => {
        memberCounts[user.membership]++;
      });

      setMemberStats(memberCounts);

    } catch (error) {
      console.error('Error fetching users:', error.message);
      showNotification(`เกิดข้อผิดพลาดในการดึงข้อมูล: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // เพิ่มฟังก์ชัน handleSearch
  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      setCurrentUserPage(1);
      fetchUsers();
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>Admin Dashboard | AI Unlocked</title>
        <meta name="description" content="จัดการระบบ AI Unlocked" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <main className="flex-grow">
        <div className="max-w-6xl mx-auto p-6">
          {/* Stats Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Date Range Selector */}
            <div className="md:col-span-3 bg-gray-900 rounded-lg p-4 mb-4">
              <div className="flex flex-wrap items-center gap-4">
                {/* Input Group */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-400">วันที่:</span>
                    <input
                      type="date"
                      value={dateRange.startDate}
                      onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                      className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-400">ถึง</span>
                    <input
                      type="date"
                      value={dateRange.endDate}
                      onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                      className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm"
                    />
                  </div>
                </div>

                {/* Divider */}
                <div className="h-6 w-px bg-gray-700"></div>

                {/* Preset Buttons */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleDatePreset('today')}
                    className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded text-sm transition-colors"
                  >
                    วันนี้
                  </button>
                  <button
                    onClick={() => handleDatePreset('yesterday')}
                    className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded text-sm transition-colors"
                  >
                    เมื่อวาน
                  </button>
                  <button
                    onClick={() => handleDatePreset('last7days')}
                    className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded text-sm transition-colors"
                  >
                    7 วันที่แล้ว
                  </button>
                  <button
                    onClick={() => handleDatePreset('thisMonth')}
                    className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded text-sm transition-colors"
                  >
                    เดือนนี้ 
                  </button>
                  <button
                    onClick={() => handleDatePreset('lastMonth')}
                    className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded text-sm transition-colors"
                  >
                    เดือนที่แล้ว
                  </button>
                </div>
              </div>
            </div>

            {/* Today's Orders -> Range Orders */}
            <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 rounded-lg p-6 border border-blue-500/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">ยอดสั่งซื้อวันนี้</p>
                  <p className="text-2xl font-bold text-blue-400 mt-1">
                    ฿{stats.todayOrders.toLocaleString()}
                  </p>
                </div>
                <div className="p-3 bg-blue-500/20 rounded-lg">
                  <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Pending Orders */}
            <div className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 rounded-lg p-6 border border-yellow-500/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">ยอดรออนุมัติ</p>
                  <p className="text-2xl font-bold text-yellow-400 mt-1">
                    ฿{stats.pendingOrders.toLocaleString()}
                  </p>
                </div>
                <div className="p-3 bg-yellow-500/20 rounded-lg">
                  <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Total Orders */}
            <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 rounded-lg p-6 border border-green-500/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">ยอดรวม</p>
                  <p className="text-2xl font-bold text-green-400 mt-1">
                    ฿{stats.totalOrders.toLocaleString()}
                  </p>
                </div>
                <div className="p-3 bg-green-500/20 rounded-lg">
                  <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-900 rounded-lg shadow-xl p-6 border border-gray-700">
            {/* Tab Navigation */}
            <div className="flex gap-4 mb-6">
              <button
                onClick={() => setActiveTab('payments')}
                className={`px-4 py-2 rounded ${
                  activeTab === 'payments' 
                    ? 'bg-yellow-500 text-black' 
                    : 'bg-gray-800 hover:bg-gray-700'
                }`}
              >
                รายการแจ้งชำระเงิน
              </button>
              <Link
                to="/admin/payment"
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded"
              >
                จัดการการชำระเงิน
              </Link>
              <button
                onClick={() => setActiveTab('users')}
                className={`px-4 py-2 rounded ${
                  activeTab === 'users' 
                    ? 'bg-yellow-500 text-black' 
                    : 'bg-gray-800 hover:bg-gray-700'
                }`}
              >
                จัดการสมาชิก
              </button>
              <Link
                to="/admin/member"
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded"
              >
                จัดการสมาชิกใหม่
              </Link>
              <button
                onClick={() => setActiveTab('announcements')}
                className={`px-4 py-2 rounded ${
                  activeTab === 'announcements' 
                    ? 'bg-yellow-500 text-black' 
                    : 'bg-gray-800 hover:bg-gray-700'
                }`}
              >
                จัดการข่าวสาร
              </button>
              <button
                onClick={() => setActiveTab('coupons')}
                className={`px-4 py-2 rounded ${
                  activeTab === 'coupons' 
                    ? 'bg-yellow-500 text-black' 
                    : 'bg-gray-800 hover:bg-gray-700'
                }`}
              >
                จัดการคูปอง
              </button>
            </div>

            {activeTab === 'payments' ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left border-b border-gray-700">
                      <th className="pb-3 px-4">วันที่</th>
                      <th className="pb-3 px-4">อีเมล</th>
                      <th className="pb-3 px-4">แพ็กเกจ</th>
                      <th className="pb-3 px-4">จำนวนเงิน</th>
                      <th className="pb-3 px-4">สถานะ</th>
                      <th className="pb-3 px-4">วันหมดอายุ</th>
                      <th className="pb-3 px-4">สลิป</th>
                      <th className="pb-3 px-4">การจัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment.id} className="border-b border-gray-800">
                        <td className="py-4 px-4">{formatDate(payment.created_at)}</td>
                        <td className="py-4 px-4">
                          {payment.profiles?.email || 'ไม่พบข้อมูล'}
                        </td>
                        <td className="py-4 px-4">
                          {payment.packages?.name || payment.package_id}
                        </td>
                        <td className="py-4 px-4">{payment.amount}฿</td>
                        <td className="py-4 px-4">
                          <span className={`px-2 py-1 rounded text-sm ${
                            payment.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            payment.payment_status === 'approved' ? 'bg-green-500/20 text-green-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {payment.payment_status === 'pending' ? 'ร��ตรวจสอบ' :
                             payment.payment_status === 'approved' ? 'อนุมัติแล้ว' :
                             'ปฏิเสธ'}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          {payment.profiles?.expires_at ? (
                            <span className={`${
                              new Date(payment.profiles.expires_at) < new Date() 
                                ? 'text-red-400' 
                                : 'text-green-400'
                            }`}>
                              {formatDate(payment.profiles.expires_at)}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="py-4 px-4">
                          {payment.slip_image && (
                            <a 
                              href={supabase.storage
                                .from('payment_slips')
                                .getPublicUrl(payment.slip_image)
                                .data.publicUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(supabase.storage
                                  .from('payment_slips')
                                  .getPublicUrl(payment.slip_image)
                                  .data.publicUrl, '_blank');
                              }}
                              className="text-blue-400 hover:underline"
                            >
                              ดูสลิป
                            </a>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {payment.payment_status === 'pending' && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleApprove(payment)}
                                className="px-3 py-1 bg-green-600 rounded hover:bg-green-700"
                              >
                                อนุมัิ
                              </button>
                              <button
                                onClick={() => updatePaymentStatus(payment.id, 'rejected')}
                                className="px-3 py-1 bg-red-600 rounded hover:bg-red-700"
                              >
                                ปฏิเสธ
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : activeTab === 'announcements' ? (
              <div className="bg-gray-900 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-4">จัดการข่าวสาร</h2>
                
                <div className="flex gap-4 mb-8">
                  <input
                    type="text"
                    value={newAnnouncement}
                    onChange={(e) => setNewAnnouncement(e.target.value)}
                    placeholder="ข้อความประกาศ..."
                    className="flex-grow px-4 py-2 bg-gray-800 rounded-lg"
                  />
                  <button
                    onClick={createAnnouncement}
                    className="px-4 py-2 bg-green-600 rounded-lg"
                  >
                    เพิ่มข่าวสาร
                  </button>
                </div>

                <div className="space-y-4">
                  {announcements.map((announcement) => (
                    <div 
                      key={announcement.id}
                      className="flex items-center justify-between p-4 bg-gray-800 rounded-lg"
                    >
                      {editingAnnouncement === announcement.id ? (
                        <input
                          type="text"
                          value={newAnnouncement || announcement.message}
                          onChange={(e) => setNewAnnouncement(e.target.value)}
                          className="flex-grow px-4 py-2 bg-gray-700 rounded-lg mr-4"
                          autoFocus
                        />
                      ) : (
                        <p>{announcement.message}</p>
                      )}
                      <div className="flex items-center gap-2">
                        <span className={announcement.is_active ? 'text-green-400' : 'text-gray-400'}>
                          {announcement.is_active ? 'กำลังแสดง' : 'ปิดการแสดง'}
                        </span>
                        <div className="flex items-center gap-1">
                          {editingAnnouncement === announcement.id ? (
                            <>
                              <button
                                onClick={() => {
                                  updateAnnouncement(announcement.id, newAnnouncement);
                                }}
                                className="px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-sm transition-colors"
                              >
                                บันทึก
                              </button>
                              <button
                                onClick={() => {
                                  setEditingAnnouncement(null);
                                  setNewAnnouncement('');
                                }}
                                className="px-3 py-1 bg-gray-600 hover:bg-gray-700 rounded text-sm transition-colors"
                              >
                                ยกเลก
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  setEditingAnnouncement(announcement.id);
                                  setNewAnnouncement(announcement.message);
                                }}
                                className="p-1.5 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 transition-colors"
                                title="แก้ไข"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => deleteAnnouncement(announcement.id)}
                                className="p-1.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors"
                                title="ลบ"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                              <button
                                onClick={() => toggleAnnouncementStatus(announcement.id, announcement.is_active)}
                                className={`px-3 py-1 rounded text-sm ${
                                  announcement.is_active 
                                    ? 'bg-red-600 hover:bg-red-700' 
                                    : 'bg-green-600 hover:bg-green-700'
                                }`}
                              >
                                {announcement.is_active ? 'ปิดการแสดง' : 'เปิดการแสดง'}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : activeTab === 'coupons' ? (
              <div className="bg-gray-900 rounded-lg p-6">
                <h2 className="text-xl font-semibold mb-6">จัดการคูปอง</h2>
                
                {/* ฟอร์มสร้างคูปองใหม่ */}
                <div className="bg-gray-800 rounded-lg p-6 mb-8">
                  <h3 className="text-lg font-medium mb-4">สร้างคูปองใหม่</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">รหัสคูปอง</label>
                      <input
                        type="text"
                        value={newCoupon.code}
                        onChange={(e) => setNewCoupon(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                        className="w-full px-3 py-2 bg-gray-700 rounded"
                        placeholder="SUMMER2024"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">ประเภทส่วนลด</label>
                      <select
                        value={newCoupon.discount_type}
                        onChange={(e) => setNewCoupon(prev => ({ ...prev, discount_type: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-700 rounded"
                      >
                        <option value="fixed">ลดเป็นจำนวนเงิน</option>
                        <option value="percentage">ลดเป็นเปอร์เซ็นต์</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">จำนวนส่วนลด</label>
                      <input
                        type="number"
                        value={newCoupon.discount_amount}
                        onChange={(e) => setNewCoupon(prev => ({ ...prev, discount_amount: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-700 rounded"
                        placeholder={newCoupon.discount_type === 'fixed' ? '100' : '10'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">ยอดขั้นต่ำ</label>
                      <input
                        type="number"
                        value={newCoupon.min_purchase}
                        onChange={(e) => setNewCoupon(prev => ({ ...prev, min_purchase: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-700 rounded"
                        placeholder="0"
                      />
                    </div>
                    {newCoupon.discount_type === 'percentage' && (
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">ส่วนลดสูงสุด</label>
                        <input
                          type="number"
                          value={newCoupon.max_discount}
                          onChange={(e) => setNewCoupon(prev => ({ ...prev, max_discount: e.target.value }))}
                          className="w-full px-3 py-2 bg-gray-700 rounded"
                          placeholder="100"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">จำนวนครั้งที่ใช้ได้</label>
                      <input
                        type="number"
                        value={newCoupon.usage_limit}
                        onChange={(e) => setNewCoupon(prev => ({ ...prev, usage_limit: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-700 rounded"
                        placeholder="1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">วันหมดอายุ</label>
                      <input
                        type="datetime-local"
                        value={newCoupon.expires_at}
                        onChange={(e) => setNewCoupon(prev => ({ ...prev, expires_at: e.target.value }))}
                        className="w-full px-3 py-2 bg-gray-700 rounded"
                      />
                    </div>
                  </div>
                  <button
                    onClick={createCoupon}
                    className="mt-4 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    สร้างคูปอง
                  </button>
                </div>

                {/* รายการคู��อง */}
                <div className="space-y-4">
                  {coupons.map((coupon) => (
                    <div 
                      key={coupon.id}
                      className="flex items-center justify-between p-4 bg-gray-800 rounded-lg"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold">{coupon.code}</span>
                          <span className={coupon.is_active ? 'text-green-400' : 'text-red-400'}>
                            ({coupon.is_active ? 'กำลังแสดง' : 'ปิดใช้งาน'})
                          </span>
                        </div>
                        <div className="text-sm text-gray-400 mt-1">
                          <p>
                            ส่วนลด: {coupon.discount_type === 'fixed' 
                              ? `${coupon.discount_amount}฿` 
                              : `${coupon.discount_amount}% (สูงสุด ${coupon.max_discount || 'ไม่จำกัด'}฿)`
                            }
                          </p>
                          <p>
                            ใช้งา���แล้ว: {coupon.usage_count}/{coupon.usage_limit} ครั้ง
                          </p>
                          {coupon.expires_at && (
                            <p>หมดอายุ: {new Date(coupon.expires_at).toLocaleString()}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleCouponStatus(coupon.id, coupon.is_active)}
                          className={`px-3 py-1 rounded text-sm ${
                            coupon.is_active 
                              ? 'bg-red-600 hover:bg-red-700' 
                              : 'bg-green-600 hover:bg-green-700'
                          }`}
                        >
                          {coupon.is_active ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
                        </button>
                        <button
                          onClick={() => deleteCoupon(coupon.id)}
                          className="p-1.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : activeTab === 'users' ? (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-400">สมาชิกทั้งหมด</p>
                        <p className="text-2xl font-bold text-white">{memberStats.total}</p>
                      </div>
                      <div className="p-3 bg-blue-500/20 rounded-lg">
                        <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-400">Free</p>
                        <p className="text-2xl font-bold text-gray-300">{memberStats.free}</p>
                      </div>
                      <div className="p-3 bg-gray-500/20 rounded-lg">
                        <span className="text-2xl">🆓</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-400">VIP</p>
                        <p className="text-2xl font-bold text-yellow-400">{memberStats.vip}</p>
                      </div>
                      <div className="p-3 bg-yellow-500/20 rounded-lg">
                        <span className="text-2xl">👑</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-400">Lifetime</p>
                        <p className="text-2xl font-bold text-green-400">{memberStats.lifetime}</p>
                      </div>
                      <div className="p-3 bg-green-500/20 rounded-lg">
                        <span className="text-2xl">♾️</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-400">Admin</p>
                        <p className="text-2xl font-bold text-red-400">{memberStats.admin}</p>
                      </div>
                      <div className="p-3 bg-red-500/20 rounded-lg">
                        <span className="text-2xl">⚡</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Search and Filter */}
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={emailSearch}
                        onChange={(e) => setEmailSearch(e.target.value)}
                        onKeyPress={handleSearch}
                        placeholder="ค้นหาด้วยอีเมล..."
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <button
                      onClick={() => {
                        setCurrentUserPage(1);
                        fetchUsers();
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      ค้นหา
                    </button>
                  </div>
                </div>

                {/* Users Table */}
                <div className="bg-gray-800 rounded-lg border border-gray-700">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left border-b border-gray-700">
                        <th className="p-4">อีเมล</th>
                        <th className="p-4">ชื่อที่แสดง</th>
                        <th className="p-4">ระดับสมาชิก</th>
                        <th className="p-4">วันที่สมัคร</th>
                        <th className="p-4">วันหมดอายุ</th>
                        <th className="p-4">การจัดการ</th>
                        <th className="p-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id} className="border-b border-gray-700 hover:bg-gray-700/50">
                          <td className="p-4">
                            {editingUserData?.id === user.id ? (
                              <input
                                type="email"
                                value={editingUserData.email}
                                onChange={(e) => setEditingUserData(prev => ({...prev, email: e.target.value}))}
                                className="w-full px-2 py-1 bg-gray-700 rounded"
                              />
                            ) : (
                              user.email
                            )}
                          </td>
                          <td className="p-4">
                            {editingUserData?.id === user.id ? (
                              <input
                                type="text"
                                value={editingUserData.display_name || ''}
                                onChange={(e) => setEditingUserData(prev => ({...prev, display_name: e.target.value}))}
                                className="w-full px-2 py-1 bg-gray-700 rounded"
                              />
                            ) : (
                              user.display_name || '-'
                            )}
                          </td>
                          <td className="p-4">
                            <div className={`flex items-center gap-2
                              ${user.membership === 'vip' && 'text-yellow-400'}
                              ${user.membership === 'lifetime' && 'text-green-400'}
                              ${user.membership === 'admin' && 'text-red-400'}
                              ${user.membership === 'free' && 'text-gray-400'}
                            `}>
                              <span>
                                {user.membership === 'free' && '🆓'}
                                {user.membership === 'vip' && '👑'}
                                {user.membership === 'lifetime' && '♾️'}
                                {user.membership === 'admin' && '⚡'}
                              </span>
                              <span>{user.membership}</span>
                            </div>
                          </td>
                          <td className="p-4">{formatDate(user.created_at)}</td>
                          <td className="p-4">
                            {editingUserData?.id === user.id ? (
                              <input
                                type="datetime-local"
                                value={editingUserData.expires_at ? new Date(editingUserData.expires_at).toISOString().slice(0, 16) : ''}
                                onChange={(e) => setEditingUserData(prev => ({...prev, expires_at: e.target.value}))}
                                className="w-full px-2 py-1 bg-gray-700 rounded"
                              />
                            ) : (
                              user.membership === 'vip' ? (
                                user.expires_at ? formatDate(user.expires_at) : '-'
                              ) : user.membership === 'lifetime' ? (
                                <span className="text-green-400">ตลอดชีพ ♾️</span>
                              ) : user.membership === 'admin' ? (
                                <span className="text-red-400">ตลอดชีพ ⚡</span>
                              ) : (
                                '-'
                              )
                            )}
                          </td>
                          <td className="p-4">
                            <select
                              value={user.membership}
                              onChange={(e) => updateMembership(user.id, e.target.value)}
                              className="px-3 py-1.5 bg-gray-700 border border-gray-600 rounded-lg"
                            >
                              <option value="free">Free</option>
                              <option value="vip">VIP</option>
                              <option value="lifetime">Lifetime</option>
                              <option value="admin">Admin</option>
                            </select>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              {editingUserData?.id === user.id ? (
                                <>
                                  <button
                                    onClick={saveUserEdit}
                                    className="p-1.5 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 transition-colors"
                                    title="บันทึก"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => setEditingUserData(null)}
                                    className="p-1.5 bg-gray-500/20 text-gray-400 rounded hover:bg-gray-500/30 transition-colors"
                                    title="ยกเลิก"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => editUser(user.id)}
                                    className="p-1.5 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 transition-colors"
                                    title="แก้ไข"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => deleteUser(user.id)}
                                    className="p-1.5 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors"
                                    title="ลบ"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  <div className="p-4 border-t border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-400">
                        แสดง {((currentUserPage - 1) * USERS_PER_PAGE) + 1} ถึง {Math.min(currentUserPage * USERS_PER_PAGE, totalUsers)} จาก {totalUsers} รายการ
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setCurrentUserPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentUserPage === 1}
                          className="px-3 py-1 bg-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-600"
                        >
                          ก่อนหน้า
                        </button>
                        <span className="px-4 py-1 bg-gray-700 rounded-lg">
                          {currentUserPage} / {Math.ceil(totalUsers / USERS_PER_PAGE)}
                        </span>
                        <button
                          onClick={() => setCurrentUserPage(prev => Math.min(prev + 1, Math.ceil(totalUsers / USERS_PER_PAGE)))}
                          disabled={currentUserPage >= Math.ceil(totalUsers / USERS_PER_PAGE)}
                          className="px-3 py-1 bg-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-600"
                        >
                          ถัดไป
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </main>

      <Footer />

      {/* เพิ่มส่วนแสดงการแจ้งเตือน */}
      {notification.show && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg transition-all transform ${
          notification.type === 'success' 
            ? 'bg-green-600' 
            : 'bg-red-600'
        }`}>
          <div className="flex items-center gap-2">
            {notification.type === 'success' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <p className="text-white">{notification.message}</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default Admin